import React, { useEffect, useState, useCallback, useRef } from 'react';
import { SERVICE_TYPES } from '../../../lib/traceability';
import './fieldBuilder.css';
import { useAuth } from '../../../context/AuthContext';
import { ownsTemplate, questionLocked } from '../../../lib/templatePermissions';
import { RequirementsEditor, VisibilityEditor, SectionWeightEditor } from './IntegrityEditors';
import {
  criticalRequirements,
  templateQuestionError,
  persistOrder,
} from '../../../lib/templateIntegrity';
import { binaryLabels, canScore, errorMessage } from '../../../lib/evaluationIntegrity';
import { useParams, useNavigate } from 'react-router-dom';
import { templatesAPI } from '../../../api/templates';
import {
  templateStatusConfig, isTemplateEditable, canCreateNewVersion, normalizeTemplateStatus,
} from '../../../lib/templateStatus';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Plus,
  GripVertical,
  Trash2,
  Edit2,
  Copy,
  Eye,
  Save,
  AlertTriangle,
  CheckCircle,
  Star,
  AlignLeft,
  Hash,
  Calendar,
  Paperclip,
  ToggleLeft,
  CheckSquare,
  CircleDot,
  Minus,
  X,
  ChevronDown,
  Layers,
  Settings,
  Upload,
  GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';

const QUESTION_TYPES = [
  { value: 'compliance', label: 'Cumple / No cumple', icon: CheckSquare },
  { value: 'existence', label: 'Existe / No existe', icon: CheckSquare },
  { value: 'info', label: 'Informativo (solo lectura)', icon: AlignLeft },
  { value: 'score_scale',      label: 'Escala de puntaje',  icon: Star },
  { value: 'single_choice',    label: 'Opción única',       icon: CircleDot },
  { value: 'multiple_choice',  label: 'Opción múltiple',    icon: CheckSquare },
  { value: 'dropdown',         label: 'Lista desplegable',  icon: ChevronDown },
  { value: 'yes_no',           label: 'Sí / No',            icon: ToggleLeft },
  { value: 'text_short',       label: 'Texto corto',        icon: Minus },
  { value: 'text_long',        label: 'Texto largo',        icon: AlignLeft },
  { value: 'numeric',          label: 'Numérico',           icon: Hash },
  { value: 'date',             label: 'Fecha',              icon: Calendar },
  { value: 'file_upload',      label: 'Archivo',            icon: Paperclip },
];

// #RecintoSeguro — Escala oficial SOEM 0-10. El `label` es la etiqueta
// del nivel (encabezados_niveles del estándar); `description` y
// `recommendation` se personalizan por criterio en el Constructor.
const DEFAULT_OPTIONS = [
  { value: 0,  label: 'No cumple',                                                description: '', recommendation: '' },
  { value: 2,  label: 'Condiciones por debajo del límite',                        description: '', recommendation: '' },
  { value: 4,  label: 'Condiciones limitadas',                                    description: '', recommendation: '' },
  { value: 6,  label: 'Implementado según estándar',                              description: '', recommendation: '' },
  { value: 8,  label: 'Implementado con resultados consistentes (últimos 6 eventos)', description: '', recommendation: '' },
  { value: 10, label: 'Condición exportable',                                     description: '', recommendation: '' },
];

function getTypeIcon(type) {
  const t = QUESTION_TYPES.find(t => t.value === type);
  return t ? t.icon : Star;
}

// #Fase1-10 — Solo los tipos calificables pueden ser bloqueantes.
function isBlockingScale(q) {
  return !!q?.is_blocking && canScore(q);
}

// ─── Sortable Section Item ───────────────────────────────────────────────────
function SortableSection({ section, active, onClick, onDelete, readOnly }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id, disabled: readOnly });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  // In readOnly mode use a plain div to avoid DnD event interference
  if (readOnly) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2.5 rounded-sm cursor-pointer transition-colors group ${active ? 'bg-soem-primary text-white' : 'hover:bg-slate-700 text-slate-300'}`}
        onClick={onClick}>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${active ? 'text-white' : 'text-slate-200'}`}>{section.name}</p>
          <p className={`text-xs ${active ? 'text-blue-200' : 'text-slate-500'}`}>{section.questions?.length || 0} preguntas</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-sm cursor-pointer transition-colors group ${active ? 'bg-soem-primary text-white' : 'hover:bg-slate-700 text-slate-300'}`}
      onClick={onClick}>
      <button {...attributes} {...listeners} className="cursor-grab opacity-40 group-hover:opacity-70 flex-shrink-0">
        <GripVertical size={14} />
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${active ? 'text-white' : 'text-slate-200'}`}>{section.name}</p>
        <p className={`text-xs ${active ? 'text-blue-200' : 'text-slate-500'}`}>{section.questions?.length || 0} preguntas</p>
      </div>
      <button onClick={e => { e.stopPropagation(); onDelete(); }}
        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 flex-shrink-0 transition-opacity">
        <Trash2 size={12} />
      </button>
    </div>
  );
}

// ─── Sortable Question Row ────────────────────────────────────────────────────
function QuestionRow({ question, active, onClick, onDuplicate, onDelete, readOnly }) {
  const TypeIcon = getTypeIcon(question.question_type);
  const typeCfg = QUESTION_TYPES.find(t => t.value === question.question_type);
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-blue-50/40 transition-colors group ${active ? 'bg-blue-50 border-l-2 border-l-soem-primary' : ''}`}
      onClick={onClick}>
      <div className="w-7 h-7 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
        <TypeIcon size={14} className="text-soem-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-soem-navy truncate">
          {question.locked && <span aria-label="Pregunta bloqueada">🔒 </span>}{question.title}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-slate-400">{typeCfg?.label}</span>
          {isBlockingScale(question) && (
            <span className="text-xs text-red-600 flex items-center gap-0.5 font-bold">
              <AlertTriangle size={9} /> Bloqueante
            </span>
          )}
        </div>
      </div>
      {!readOnly && (
        <div className="flex items-center gap-1 transition-opacity">
          <button onClick={e => { e.stopPropagation(); onDuplicate(); }} className="p-1 text-slate-400 hover:text-soem-primary" aria-label="Duplicar"><Copy size={12} /></button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={12} /></button>
        </div>
      )}
    </div>
  );
}

function SortableQuestion({ question, active, onClick, onDuplicate, onDelete, readOnly }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: question.id, disabled: readOnly });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const TypeIcon = getTypeIcon(question.question_type);
  const typeCfg = QUESTION_TYPES.find(t => t.value === question.question_type);
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-blue-50/40 transition-colors group ${active ? 'bg-blue-50 border-l-2 border-l-soem-primary' : ''}`}
      onClick={onClick}>
      <button {...attributes} {...listeners} className="cursor-grab text-slate-300 group-hover:text-slate-400 flex-shrink-0">
        <GripVertical size={14} />
      </button>
      <div className="w-7 h-7 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
        <TypeIcon size={14} className="text-soem-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-soem-navy truncate">
          {question.locked && <span aria-label="Pregunta bloqueada">🔒 </span>}{question.title}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-slate-400">{typeCfg?.label}</span>
          {isBlockingScale(question) && (
            <span className="text-xs text-red-600 flex items-center gap-0.5 font-bold">
              <AlertTriangle size={9} /> Bloqueante
            </span>
          )}
        </div>
      </div>
      {!readOnly && <div className="flex items-center gap-1 transition-opacity">
        <button onClick={e => { e.stopPropagation(); onDuplicate(); }} className="p-1 text-slate-400 hover:text-soem-primary" aria-label="Duplicar"><Copy size={12} /></button>
        <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={12} /></button>
      </div>}
    </div>
  );
}

// ─── Question Editor Panel ────────────────────────────────────────────────────
function QuestionEditor({ question, tid, sid, onSaved, readOnly,
  previousQuestions = [],
  allQuestions = [],
}) {
  const [form, setForm] = useState({ ...question });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const saveTimeoutRef = React.useRef(null);
  // #Fase0-B3 — revisión del formulario: si el usuario sigue escribiendo
  // mientras el guardado va en vuelo, al terminar NO se marca como guardado
  // (antes setDirty(false) cancelaba el autoguardado pendiente y ese último
  // cambio se quedaba solo en pantalla).
  const editRevRef = React.useRef(0);

  useEffect(() => { setForm({ ...question }); setDirty(false);
    // #Fase1-10 — Cambiar de pregunta reinicia; una respuesta de guardado no pisa ediciones en vuelo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  const update = (changes) => {
    if (readOnly) return;
    editRevRef.current += 1;
    setForm(f => ({ ...f, ...changes }));
    setDirty(true);
  };

  // #Fase1-10 — Solo escala y cumplimiento califican por defecto.
  const changeType = (question_type) => {
    if (question_type === form.question_type) return;
    if (['score_scale', 'compliance'].includes(question_type)) update({ question_type, scoring_enabled: true });
    else update({ question_type, scoring_enabled: false, is_blocking: false });
  };

  const validationError = templateQuestionError(form, previousQuestions, allQuestions);
  const doSave = useCallback(
    async (formData) => {
      if (readOnly) return false;
      const error = templateQuestionError(formData, previousQuestions, allQuestions);
      if (error) {
        toast.error(error);
        return false;
      }
      const rev = editRevRef.current;
    setSaving(true);
    try {
      // Defensa: nos aseguramos de que `question_type` viaje en el
      // body con un valor válido. Antes se enviaba {type: '...'} pero
      // el backend ignora `type` y lee body.question_type — los
      // cambios de tipo nunca llegaban a la BD y todas las preguntas
      // quedaban como score_scale (#FvrWJPZV).
        // #Fase1-10 — el mapeo formulario → API (tipos y campos que la
        // API sí persiste) vive solo en templatesAPI.updateQuestion.
      const r = await templatesAPI.updateQuestion(tid, sid, formData.id, formData);
      onSaved({ ...formData, ...r.data });
        if (editRevRef.current === rev) {
          setForm((current) => ({ ...current, ...r.data }));
          setDirty(false);
        }
        return true;
    } catch (err) {
      // Mostrar el motivo real del backend en vez de un mensaje genérico
        toast.error(errorMessage(err, 'Error al guardar pregunta'));
        return false;
    }
    finally { setSaving(false); }
    },
    [allQuestions, previousQuestions, tid, sid, onSaved, readOnly]
  );

  // Auto-save with debounce
  useEffect(() => {
    if (!dirty || readOnly || validationError) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => doSave(form), 1200);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [form, dirty, validationError, readOnly, doSave]);

  const handleSave = async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    if (await doSave(form)) toast.success('Pregunta guardada');
  };

  const addOption = () => {
    const opts = [...(form.options || [])];
    opts.push({ id: Date.now().toString(), value: opts.length, label: `Opción ${opts.length + 1}`, description: '', recommendation: '' });
    update({ options: opts });
  };

  const updateOption = (idx, field, val) => {
    const opts = [...(form.options || [])];
    opts[idx] = { ...opts[idx], [field]: val };
    update({ options: opts });
  };

  const removeOption = (idx) => {
    const opts = (form.options || []).filter((_, i) => i !== idx);
    update({ options: opts });
  };

  const loadDefaultScale = () => {
    update({ options: DEFAULT_OPTIONS.map(o => ({ ...o, id: Date.now().toString() + Math.random() })) });
  };

  // #Fase1-10 — Estos tipos permiten configurar calificación y bloqueo.
  const isScale = ['score_scale', 'compliance', 'existence', 'yes_no'].includes(form.question_type);
  const hasOptions = ['score_scale', 'single_choice', 'multiple_choice', 'dropdown'].includes(form.question_type);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Settings size={16} className="text-soem-primary" />
          <span className="font-bold text-soem-navy text-sm">Configurar pregunta</span>
        </div>
        {dirty && !readOnly && (
          <span className="text-xs text-orange-500 font-medium flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-orange-400 rounded-full" /> Sin guardar
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Common fields */}
        <div className="space-y-3">
          <div>
            <label htmlFor="f-título" className="soem-label block mb-1">Título <span className="text-red-500">*</span></label>
            <input id="f-título" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              value={form.title === 'Nueva pregunta' ? '' : form.title} 
              placeholder="Escribe el título de la pregunta..."
              onChange={e => update({ title: e.target.value })} disabled={readOnly} data-testid="q-title" />
          </div>
          <div>
            <label htmlFor="f-descripción-enfoque" className="soem-label block mb-1">Descripción / Enfoque</label>
            <textarea id="f-descripción-enfoque" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-soem-primary"
              rows={2} value={form.description || ''} onChange={e => update({ description: e.target.value })} disabled={readOnly} data-testid="q-desc" />
          </div>
          {/* #RecintoSeguro — estándar de recinto seguro del criterio.
              Se muestra en una caja gris al contestar la evaluación. */}
          <div>
            <label htmlFor="f-recinto-seguro" className="soem-label block mb-1">Recinto Seguro</label>
            <textarea id="f-recinto-seguro" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-soem-primary"
              rows={2} placeholder="Describe el estándar de recinto seguro de este criterio..."
              value={form.recinto_seguro || ''} onChange={e => update({ recinto_seguro: e.target.value })} disabled={readOnly} data-testid="q-recinto" />
          </div>
          <div>
            <label htmlFor="f-tipo-de-respuesta" className="soem-label block mb-1">Tipo de respuesta</label>
            <div className="grid grid-cols-3 gap-1.5">
              {QUESTION_TYPES.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.value} type="button" disabled={readOnly}
                    onClick={() => changeType(t.value)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-sm border text-xs font-medium transition-colors ${form.question_type === t.value ? 'border-soem-primary bg-blue-50 text-soem-primary' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                    data-testid={`type-${t.value}`}>
                    <Icon size={14} />
                    <span className="leading-tight text-center" style={{fontSize:'10px'}}>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Toggles */}
        <div className="space-y-2">
          <p className="soem-label">Opciones</p>
          {[
            { key: 'allow_na', label: 'Permite No aplica' },
            { key: 'is_critical', label: 'Reactivo crítico' },
            { key: 'is_required', label: '¿Es requerida?' },
            ...(isScale ? [{ key: 'scoring_enabled', label: '¿Califica?' }] : []),
            ...(canScore(form) ? [{ key: 'is_blocking', label: '¿Es bloqueante?' }] : []),
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-600">{label}</span>
              <button type="button" disabled={readOnly}
                onClick={() => {
                  const next = !(form[key] ?? key === 'allow_na');
                  // Un reactivo que no suma no puede bloquear (C4).
                  if (key === 'scoring_enabled' && !next) update({ scoring_enabled: false, is_blocking: false });
                  else if (key === 'is_critical' && next) {
                    update({
                      is_critical: true,
                      requirements: criticalRequirements(form.requirements),
                    });
                  } else update({ [key]: next });
                }}
                className={`w-10 h-5 rounded-full relative transition-colors ${
                  (form[key] ?? key === 'allow_na') ? 'bg-soem-primary' : 'bg-slate-300'
                }`}>
                <span
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    (form[key] ?? key === 'allow_na') ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

        <label className="block text-sm">¿Requiere evidencia?
          <select disabled={readOnly} value={form.requirements?.photo || 'never'}
            onChange={e => update({ requirements: { ...form.requirements, photo: e.target.value } })}>
            <option value="never">No</option><option value="always">Siempre</option>
            <option value="below_standard">Solo si no cumple</option>
          </select>
        </label>

        {/* #Fase1-1 — Requisitos y dependencias viajan con nombres de la API. */}

        <label className="block text-sm">
          Código
          <input
            className="w-full border p-2 rounded-sm"
            value={form.code || ''}
            disabled={readOnly}
            onChange={(event) => update({ code: event.target.value })}
          />

        </label>

        {form.question_type === 'yes_no' && (
          <label className="block text-sm">
            Respuesta favorable {form.scoring_enabled && '(obligatoria)'}
            <select
              className="w-full border p-2"
              disabled={readOnly}
              value={form.favorable_answer || ''}
              onChange={(event) => update({ favorable_answer: event.target.value || null })}
            >
              <option value="">Seleccionar</option>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select>
          </label>
        )}
        {/* #Fase3-A3 — Se conserva el requisito en el snapshot de cada nueva evaluación. */}
        <fieldset disabled={readOnly} className="space-y-2 border p-3">
          <legend>Requiere proveedor del evento</legend>
          {Object.entries(SERVICE_TYPES).map(([type, label]) => (
            <label key={type} className="block text-sm">
              <input type="checkbox" checked={form.requires_provider?.service_types?.includes(type) || false}
                onChange={(event) => {
                  const current = form.requires_provider?.service_types || [];
                  const service_types = event.target.checked
                    ? [...current, type] : current.filter((value) => value !== type);
                  update({ requires_provider: service_types.length ? { service_types } : null });
                }} /> {label}
            </label>
          ))}
        </fieldset>
        <RequirementsEditor
          value={form.requirements}
          disabled={readOnly}
          onChange={(requirements) => update({ requirements })}
        />
        <VisibilityEditor
          value={form.visible_if}
          previousQuestions={previousQuestions}
          disabled={readOnly}
          onChange={(visible_if) => update({ visible_if })}
        />
        {validationError && (
          <p role="alert" className="text-sm text-red-700">
            {validationError}
          </p>
        )}
        {/* Type-specific fields */}
        {/* #Fase1-10 — Los binarios calificables usan 10/0 según su respuesta favorable. */}

        {form.question_type === 'numeric' && (
          <div className="space-y-3">
            <p className="soem-label">Rango numérico</p>
            <div className="grid grid-cols-2 gap-3">
              {[['min_value', 'Mínimo'], ['max_value', 'Máximo']].map(([k, l]) => (
                <div key={k}>
                  <label htmlFor={`f-range-${k}`} className="text-xs text-slate-500 block mb-1">{l}</label>
                  <input id={`f-range-${k}`} type="number" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                    value={form[k] ?? ''} onChange={e => update({ [k]: e.target.value === '' ? null : Number(e.target.value) })} disabled={readOnly} />
                </div>
              ))}
            </div>
          </div>
        )}

        {['text_short', 'text_long', 'date', 'file_upload'].includes(form.question_type) && (
          <div>
            <label htmlFor="f-instrucción-para-el-evaluador" className="soem-label block mb-1">Instrucción para el evaluador</label>
            <input id="f-instrucción-para-el-evaluador" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Ej: Sube una foto del extintor..." value={form.placeholder || ''}
              onChange={e => update({ placeholder: e.target.value })} disabled={readOnly} />
          </div>
        )}

        {/* Preview de cómo se verá el campo cuando un Levantador
            responda la pregunta (#BnPI5DGC). Marco vio "campo abierto"
            para tipo fecha porque sólo había placeholder; ahora se
            muestra el control real (date picker, textarea, file...). */}
        {['text_short', 'text_long', 'date', 'file_upload', 'numeric'].includes(form.question_type) && (
          <div className="border border-slate-200 rounded-sm bg-slate-50 p-3 space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Vista previa de la respuesta</p>
            {form.question_type === 'date' && (
              <input type="date" disabled
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" />
            )}
            {form.question_type === 'text_short' && (
              <input type="text" disabled placeholder={form.placeholder || 'Texto corto'}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" />
            )}
            {form.question_type === 'text_long' && (
              <textarea disabled rows={3} placeholder={form.placeholder || 'Texto largo'}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white resize-none" />
            )}
            {form.question_type === 'file_upload' && (
              <div className="w-full px-3 py-3 border-2 border-dashed border-slate-300 rounded-sm text-xs text-slate-400 bg-white text-center">
                📎 Adjuntar archivo
              </div>
            )}
            {form.question_type === 'numeric' && (
              <input type="number" disabled placeholder={form.placeholder || '0'}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" />
            )}
          </div>
        )}

        {/* Options editor */}
        {hasOptions && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="soem-label">Opciones</p>
              {form.question_type === 'score_scale' && !readOnly && (
                <button onClick={loadDefaultScale}
                  className="text-xs text-soem-primary hover:underline">Cargar escala 0-10</button>
              )}
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(form.options || []).map((opt, idx) => (
                <div key={opt.id || idx} className="border border-slate-200 rounded-sm p-2.5 bg-slate-50 space-y-1.5">
                  {/* Fila Valor + Etiqueta del valor — con mini-labels para
                      que quede claro qué es cada campo (#RecintoSeguro). */}
                  <div className="flex gap-2 items-end">
                    <div className="w-16 flex-shrink-0">
                      <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Valor</label>
                      <input type="number" className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-soem-primary"
                        placeholder="0" value={opt.value ?? ''} onChange={e => updateOption(idx, 'value', +e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">
                        Etiqueta del valor
                      </label>
                      <input className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-soem-primary"
                        placeholder={form.question_type === 'score_scale' ? 'Ej: No cumple, Implementado según estándar...' : 'Etiqueta'}
                        value={opt.label || ''} onChange={e => updateOption(idx, 'label', e.target.value)} disabled={readOnly} />
                    </div>
                    {!readOnly && (
                      <button onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600 flex-shrink-0 mb-1.5" aria-label="Eliminar opción"><Trash2 size={12} /></button>
                    )}
                  </div>
                  {form.question_type === 'score_scale' && (
                    <>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Descripción del estado</label>
                        <input className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-soem-primary"
                          placeholder="Qué significa este nivel en este criterio..." value={opt.description || ''} onChange={e => updateOption(idx, 'description', e.target.value)} disabled={readOnly} />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Recomendación automática</label>
                        <input className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-soem-primary"
                          placeholder="Recomendación que se mostrará al elegir este valor..." value={opt.recommendation || ''} onChange={e => updateOption(idx, 'recommendation', e.target.value)} disabled={readOnly} />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            {!readOnly && (
              <button onClick={addOption}
                className="w-full border-2 border-dashed border-slate-300 rounded-sm py-2 text-xs text-slate-400 hover:border-soem-primary hover:text-soem-primary transition-colors flex items-center justify-center gap-1">
                <Plus size={12} /> Agregar opción
              </button>
            )}
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="px-4 py-3 border-t border-slate-200 bg-white flex-shrink-0 flex items-center gap-2">
          {saving && <span className="text-xs text-slate-400 flex items-center gap-1"><span className="animate-spin rounded-full h-3 w-3 border-2 border-soem-primary border-t-transparent" /> Guardando...</span>}
          {!saving && !dirty && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={12} /> Guardado</span>}
          {!saving && dirty && <span className="text-xs text-orange-500 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-orange-400 rounded-full" /> Auto-guardado...</span>}
          <div className="flex-1" />
          <button onClick={handleSave} disabled={saving || !dirty}
            className="soem-btn-primary px-4 py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="save-question-btn">
            <Save size={14} /> Guardar
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Form Preview Modal ───────────────────────────────────────────────────────
function FormPreview({ template, onClose }) {
  const [activeSection, setActiveSection] = useState(template.sections?.[0]?.id || null);
  const section = template.sections?.find(s => s.id === activeSection);

  const renderQuestion = (q) => {
    const TypeIcon = getTypeIcon(q.question_type);
    return (
      <div key={q.id} className="soem-card p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
            <TypeIcon size={14} className="text-soem-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-soem-navy text-sm">{q.title}</p>
              {isBlockingScale(q) && <span className="text-xs text-red-600 font-bold flex items-center gap-0.5"><AlertTriangle size={9} /> BLOQUEANTE</span>}
              {q.is_required && <span className="text-red-500 text-xs">*</span>}
            </div>
            {q.description && <p className="text-slate-500 text-xs mt-0.5">{q.description}</p>}
            {/* #RecintoSeguro — estándar de recinto seguro del criterio */}
            {q.recinto_seguro && (
              <div className="mt-2 p-2 rounded-sm text-xs border-l-4 bg-slate-100 border-slate-400 text-slate-700">
                <p className="font-semibold uppercase tracking-wide opacity-70 mb-0.5">Recinto Seguro:</p>
                <p className="leading-relaxed">{q.recinto_seguro}</p>
              </div>
            )}
            <div className="mt-2">
              {q.question_type === 'score_scale' && (
                <div className="mt-2 space-y-2">
                  {/* Botones con el VALOR numérico (la etiqueta larga va
                      debajo y en el tooltip, para no desbordar). */}
                  <div className="flex flex-wrap gap-1.5">
                    {(q.options || []).map(opt => (
                      <div key={opt.id} className="group relative">
                        <button className="w-10 h-10 rounded-sm border-2 border-slate-200 text-slate-600 text-sm font-bold hover:border-soem-primary transition-colors flex items-center justify-center">
                          {opt.value ?? opt.label}
                        </button>
                        <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20">
                          <div className="bg-gray-900 text-white text-xs rounded p-2 w-56 shadow-lg">
                            <p className="font-bold text-sm mb-1">
                              {opt.value}{opt.label ? ` · ${opt.label}` : ''}
                            </p>
                            {opt.description && <p className="font-medium text-yellow-300">{opt.description}</p>}
                            {opt.recommendation && <p className="mt-1 text-gray-300 leading-relaxed">{opt.recommendation}</p>}
                            {!opt.description && !opt.recommendation && <p className="text-gray-400">Sin descripción</p>}
                          </div>
                          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Leyenda de etiquetas por valor */}
                  {(q.options || []).some(o => o.label) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
                      {(q.options || []).map(opt => (
                        <span key={`leg-${opt.id}`} className="inline-flex items-center gap-1">
                          <span className="font-bold text-slate-700">{opt.value}</span>
                          <span>{opt.label}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {q.question_type === 'info' && (
                <p className="p-3 bg-blue-50 text-sm">Informativo · Solo lectura</p>
              )}
              {binaryLabels[q.question_type] && (
                <div className="flex gap-3 mt-2">
                  <button
                    className="px-6 py-2 border-2 border-green-300 text-green-700 rounded-sm text-sm font-semibold"
                  >
                    {binaryLabels[q.question_type][0]}
                  </button>
                  <button className="px-6 py-2 border-2 border-red-300 text-red-700 rounded-sm text-sm font-semibold">
                    {binaryLabels[q.question_type][1]}
                  </button>
                </div>
              )}
              {['single_choice', 'multiple_choice'].includes(q.question_type) && (
                <div className="space-y-2 mt-2">
                  {(q.options || []).map((opt, i) => (
                    <label key={i} className="flex items-center gap-2 cursor-pointer">
                      <div className={`w-4 h-4 border-2 border-slate-300 ${q.question_type === 'single_choice' ? 'rounded-full' : 'rounded-sm'} flex-shrink-0`} />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.question_type === 'dropdown' && (
                <select className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" disabled>
                  <option value="">Seleccionar...</option>
                  {(q.options || []).map((opt, i) => (
                    <option key={i} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}
              {['text_short', 'numeric', 'date'].includes(q.question_type) && (
                <input className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-sm text-sm bg-slate-50" disabled
                  placeholder={q.placeholder || `Ingresa ${q.question_type === 'date' ? 'la fecha' : q.question_type === 'numeric' ? 'el número' : 'tu respuesta'}...`} />
              )}
              {q.question_type === 'text_long' && (
                <textarea className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-sm text-sm bg-slate-50 resize-none" disabled rows={3}
                  placeholder={q.placeholder || 'Describe en detalle...'} />
              )}
              {q.question_type === 'file_upload' && (
                <div className="mt-2 border-2 border-dashed border-slate-300 rounded-sm p-4 text-center text-slate-400 text-sm">
                  <Paperclip size={18} className="mx-auto mb-1" />
                  {q.placeholder || 'Subir archivo...'}
                </div>
              )}
            </div>
            {q.allows_notes && (
              <textarea className="w-full mt-2 px-3 py-1.5 border border-slate-200 rounded-sm text-xs text-slate-400 resize-none bg-slate-50" rows={2} disabled placeholder="Notas opcionales..." />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex">
      <div className="flex flex-col bg-white flex-1 overflow-hidden">
        {/* Header */}
        <div className="bg-soem-navy px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white font-bold">Vista previa — {template.name}</h2>
            <p className="text-slate-400 text-xs mt-0.5">Solo visual — no guarda datos</p>
          </div>
          <button aria-label="Cerrar" onClick={onClose} className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2">
            <X size={14} /> Cerrar vista previa
          </button>
        </div>
        <div className="flex flex-1 overflow-hidden">
          {/* Section tabs */}
          <div className="w-52 bg-slate-50 border-r border-slate-200 overflow-y-auto flex-shrink-0">
            {(template.sections || []).map(s => (
              <button key={s.id} onClick={() => setActiveSection(s.id)}
                className={`w-full text-left px-4 py-3 text-sm border-b border-slate-100 transition-colors ${s.id === activeSection ? 'bg-soem-primary text-white font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}>
                {s.name}
                <span className={`block text-xs mt-0.5 ${s.id === activeSection ? 'text-blue-200' : 'text-slate-400'}`}>{s.questions?.length || 0} preguntas</span>
              </button>
            ))}
          </div>
          {/* Questions */}
          <div className="flex-1 overflow-y-auto p-6">
            {section ? (
              <div className="max-w-2xl mx-auto space-y-4">
                <h2 className="text-xl font-bold text-soem-navy mb-4">{section.name}</h2>
                {section.questions?.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">Esta sección no tiene preguntas.</p>
                ) : (
                  section.questions.map(q => renderQuestion(q))
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-400">Selecciona una sección para previsualizar</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Activate Modal ───────────────────────────────────────────────────────────
function ActivateModal({ template, onActivate, onClose, allowDefault }) {
  const [setDefault, setSetDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  // #Fase0-B3 / C6 — si este borrador es una versión nueva, al activarlo la
  // API retira a la versión anterior (si sigue activa), le pasa "por
  // defecto" y la reemplaza en las plantillas permitidas de los proyectos.
  const [parent, setParent] = useState(null);
  useEffect(() => {
    if (!template.parent_id) return;
    let alive = true;
    templatesAPI.get(template.parent_id)
      .then(r => { if (alive) setParent(r.data); })
      .catch(() => { if (alive) setParent({ unknown: true }); });
    return () => { alive = false; };
  }, [template.parent_id]);
  const parentStatus = normalizeTemplateStatus(parent?.status);
  const willRetireParent = !!template.parent_id && (!parent || parent.unknown || parentStatus === 'activa');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="px-5 py-4 border-b border-slate-200 bg-soem-navy">
          <h3 className="font-bold text-white">Activar plantilla</h3>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-slate-600 text-sm">
            ¿Activar <strong>"{template.name}"</strong>{template.version ? ` (v${template.version})` : ''}? Una vez activa no podrá editarse directamente.
            Para hacer cambios futuros deberás crear una nueva versión.
          </p>
          {willRetireParent && (
            <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-xs text-orange-800 space-y-1" data-testid="activate-retire-warning">
              <p className="font-semibold flex items-center gap-1"><AlertTriangle size={12} /> La versión anterior quedará retirada</p>
              <p>
                {parent?.version ? `La v${parent.version}` : 'La versión anterior'} ya no se ofrecerá para evaluaciones nuevas.
                Las evaluaciones que ya la usan (incluidas las que están en curso) siguen con su versión.
              </p>
              <p>
                Los proyectos que la tenían permitida pasarán a esta versión
                {parent?.is_default && !template.owner_id ? ', y esta quedará como plantilla por defecto' : ''}.
              </p>
            </div>
          )}
          {allowDefault && <label
            className="flex items-start gap-3 cursor-pointer bg-yellow-50 border border-yellow-200 rounded-sm p-3">
            <input type="checkbox" checked={setDefault} onChange={e => setSetDefault(e.target.checked)} className="mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">Establecer como plantilla por defecto</p>
              <p className="text-xs text-yellow-600 mt-0.5">Todas las evaluaciones nuevas usarán esta plantilla automáticamente.</p>
            </div>
          </label>}
          <div className="flex gap-3">
            <button disabled={saving}
              onClick={async () => { setSaving(true); await onActivate(setDefault); }}
              className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="confirm-activate-btn">
              <CheckCircle size={14} /> {saving ? 'Activando...' : 'Activar'}
            </button>
            <button onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Builder ─────────────────────────────────────────────────────────────
export default function TemplateBuilder() {
  const { activeUser, user } = useAuth();
  const actor = activeUser || user;
  const [mobileTab, setMobileTab] = useState('sections');
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const reorderLock = useRef(false);
  const [reordering, setReordering] = useState(false);
  const [addingSection, setAddingSection] = useState(false);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  // #RecintoSeguro — importador de criterios desde archivo JSON
  const [importingJson, setImportingJson] = useState(false);
  const jsonFileRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    try {
      const r = await templatesAPI.get(id);
      setTemplate(r.data);
      const sections = r.data.sections || [];
      // Use functional setState to avoid stale closure — always pick a valid section
      setActiveSectionId(prev => {
        if (sections.length === 0) return null;
        if (prev && sections.some(s => s.id === prev)) return prev;
        return sections[0].id;
      });
      // Auto-select the first question of the first section so the right panel
      // shows immediately without requiring a manual click
      setActiveQuestionId(prev => {
        if (sections.length === 0) return null;
        const firstSection = sections[0];
        const firstQuestion = firstSection?.questions?.[0];
        if (!firstQuestion) return null;
        // Keep current if still valid in any section
        const allQuestions = sections.flatMap(s => s.questions || []);
        if (prev && allQuestions.some(q => q.id === prev)) return prev;
        return firstQuestion.id;
      });
    } catch { toast.error('Error al cargar plantilla'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // El backend devuelve los estados en español (activa/archivada). Antes
  // esta línea solo comparaba contra inglés (active/archived), así que una
  // plantilla "activa" se trataba como editable: el autoguardado disparaba
  // y el backend lo rechazaba con "Error al guardar pregunta".
  // #Fase0-B3 — solo el borrador se edita (activa, retirada y archivada son
  // de solo lectura), igual que _check_editable en la API.
  const owned = ownsTemplate(actor, template);
  const readOnly = !!template && (!isTemplateEditable(template.status) || !owned);
  const hasLocked = (section) => actor?.role !== 'super_admin' && section?.questions?.some((q) => q.locked);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const activeSection = template?.sections?.find(s => s.id === activeSectionId);
  const allQuestions = template?.sections?.flatMap((section) => section.questions || []) || [];
  const activeIndex = allQuestions.findIndex((question) => question.id === activeQuestionId);
  const previousQuestions = allQuestions.slice(0, activeIndex);
  const activeQuestion = activeSection?.questions?.find(q => q.id === activeQuestionId);

  const addSection = async () => {
    if (addingSection) return;
    setAddingSection(true);
    try {
      const r = await templatesAPI.addSection(id, `Sección ${(template.sections?.length || 0) + 1}`, template.sections?.length || 0);
      const newSec = r.data;
      setTemplate(t => ({ ...t, sections: [...(t.sections || []), newSec] }));
      setActiveSectionId(newSec.id);
      setMobileTab('questions');
      setActiveQuestionId(null);
    } catch { toast.error('Error al agregar sección'); }
    finally { setAddingSection(false); }
  };

  const deleteSection = async (sid) => {
    if (readOnly || hasLocked(template.sections.find((section) => section.id === sid))) return;
    if (!window.confirm('¿Eliminar esta sección y todas sus preguntas?')) return;
    try {
      await templatesAPI.deleteSection(id, sid);
      setTemplate(t => {
        const secs = t.sections.filter(s => s.id !== sid);
        return { ...t, sections: secs };
      });
      if (activeSectionId === sid) {
        const remaining = template.sections.filter(s => s.id !== sid);
        setActiveSectionId(remaining[0]?.id || null);
        setActiveQuestionId(null);
      }
    } catch (error) { toast.error(errorMessage(error, 'No se pudo eliminar: revisa las preguntas dependientes.')); }
  };

  const handleSectionDragEnd = async ({ active: a, over }) => {
    if (readOnly || template.sections.some(hasLocked) || !over || a.id === over.id) return;
    const secs = template.sections || [];
    const from = secs.findIndex(s => s.id === a.id);
    const to = secs.findIndex(s => s.id === over.id);
    const newSecs = arrayMove(secs, from, to);
    await persistOrder({
      lock: reorderLock,
      onPendingChange: setReordering,
      before: secs,
      after: newSecs,
      apply: (sections) => setTemplate((current) => ({ ...current, sections })),
      save: () => templatesAPI.reorderSections(id, newSecs.map(section => section.id)),
      onError: (error) => toast.error(errorMessage(error, 'No se pudo reordenar las secciones.')),
    });
  };

  const addQuestion = async () => {
    if (!activeSectionId || addingQuestion) return;
    setAddingQuestion(true);
    try {
      const r = await templatesAPI.addQuestion(id, activeSectionId);
      const newQ = r.data;
      setTemplate(t => ({
        ...t, sections: t.sections.map(s =>
          s.id === activeSectionId ? { ...s, questions: [...(s.questions || []), newQ] } : s
        )
      }));
      setActiveQuestionId(newQ.id);
      setMobileTab('editor');
    } catch { toast.error('Error al agregar pregunta'); }
    finally { setAddingQuestion(false); }
  };

  const deleteQuestion = async (qid) => {
    if (readOnly || questionLocked(actor, activeSection.questions.find((q) => q.id === qid))) return;
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    try {
      await templatesAPI.deleteQuestion(id, activeSectionId, qid);
      setTemplate(t => ({
        ...t, sections: t.sections.map(s =>
          s.id === activeSectionId ? { ...s, questions: (s.questions || []).filter(q => q.id !== qid) } : s
        )
      }));
      if (activeQuestionId === qid) setActiveQuestionId(null);
    } catch (error) { toast.error(errorMessage(error, 'No se pudo eliminar: revisa las preguntas dependientes.')); }
  };

  // #RecintoSeguro — Importa textos de criterios desde un archivo JSON.
  // Empareja cada criterio con su pregunta y rellena enfoque, recinto
  // seguro, y los niveles (etiqueta/descripción/recomendación).
  const handleImportJsonClick = () => {
    if (jsonFileRef.current) jsonFileRef.current.click();
  };

  const handleImportJsonFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-seleccionar el mismo archivo
    if (!file) return;
    if (importingJson) return;
    let json;
    try {
      const text = await file.text();
      json = JSON.parse(text);
    } catch {
      toast.error('El archivo no es un JSON válido');
      return;
    }
    if (!json || !Array.isArray(json.criterios) || json.criterios.length === 0) {
      toast.error('El JSON no contiene un arreglo "criterios"');
      return;
    }
    const numSecciones = Array.isArray(json.secciones) ? json.secciones.length : 0;
    if (!window.confirm(
      `¿Importar la plantilla completa desde "${file.name}"?\n\n` +
      `Se importarán ${json.criterios.length} criterios` +
      (numSecciones ? ` en ${numSecciones} secciones` : '') + `.\n\n` +
      `Las secciones y preguntas que falten se crearán; las que ya ` +
      `existan (por título) se actualizarán conservando sus respuestas.`
    )) return;

    setImportingJson(true);
    const toastId = toast.loading('Importando plantilla desde JSON...');
    try {
      const r = await templatesAPI.importCriteria(id, json);
      const d = r.data || {};
      toast.success(
        d.message ||
        `${(d.questions_created ?? 0) + (d.questions_enriched ?? 0)} criterios importados`,
        { id: toastId, duration: 8000 }
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
      toast.error(`Error al importar: ${detail}`, { id: toastId, duration: 10000 });
    } finally {
      setImportingJson(false);
    }
  };

  const duplicateQuestion = async (q) => {
    if (readOnly || questionLocked(actor, q)) return;
    try {
      const newQ = { ...q, id: undefined, title: `${q.title} (copia)` };
      const r = await templatesAPI.addQuestion(id, activeSectionId);
      const created = r.data;
      await templatesAPI.updateQuestion(id, activeSectionId, created.id, { ...newQ, id: created.id });
      load();
    } catch { toast.error('Error al duplicar pregunta'); }
  };

  const handleQuestionDragEnd = async ({ active: a, over }) => {
    if (readOnly || hasLocked(activeSection) || !over || a.id === over.id || !activeSection) return;
    const qs = activeSection.questions || [];
    const from = qs.findIndex(q => q.id === a.id);
    const to = qs.findIndex(q => q.id === over.id);
    const newQs = arrayMove(qs, from, to);
    await persistOrder({
      lock: reorderLock,
      onPendingChange: setReordering,
      before: qs,
      after: newQs,
      apply: (questions) =>
        setTemplate((current) => ({
          ...current,
          sections: current.sections.map((section) =>
            section.id === activeSectionId ? { ...section, questions } : section
          ),
        })),
      save: () =>
        templatesAPI.reorderQuestions(id, activeSectionId, newQs.map((question) => question.id)
        ),
      onError: (error) => toast.error(errorMessage(error, 'No se pudo reordenar las preguntas.')),
    });
  };

  const handleQuestionSaved = (updatedQ) => {
    setTemplate(t => ({
      ...t, sections: t.sections.map(s =>
        s.id === activeSectionId ? {
          ...s, questions: s.questions.map(q => q.id === updatedQ.id ? updatedQ : q)
        } : s
      )
    }));
    setSavedAt(new Date());
  };

  const saveSectionWeight = async (weight) => {
    try {
      await templatesAPI.updateSection(id, activeSection.id, { weight });
      setTemplate((current) => ({
        ...current,
        sections: current.sections.map((section) =>
          section.id === activeSection.id ? { ...section, weight } : section
        ),
      }));
      return true;
    } catch (error) {
      toast.error(errorMessage(error, 'Error al guardar peso'));
      return false;
    }
  };

  const saveSectionName = async (sid, name) => {
    try {
      await templatesAPI.updateSection(id, sid, { name });
      setTemplate(t => ({ ...t, sections: t.sections.map(s => s.id === sid ? { ...s, name } : s) }));
      setEditingSection(null);
    } catch { toast.error('Error al guardar'); }
  };

  const handleActivate = async (setDefault) => {
    try {
      if (readOnly) return;
      await templatesAPI.activate(id, actor?.role === 'super_admin' && !template.owner_id && setDefault);
      toast.success('Plantilla activada');
      setShowActivate(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al activar plantilla');
      setShowActivate(false);
    }
  };

  // #Fase0-B3 / C6 — "Nueva versión": borrador hijo con los mismos ids de
  // secciones y preguntas, para que las respuestas y comparativas sigan
  // emparejando. A diferencia de "Duplicar", al activarlo retira a esta.
  const handleNewVersion = async () => {
    if (creatingVersion) return;
    setCreatingVersion(true);
    try {
      const r = await templatesAPI.newVersion(template.id);
      toast.success(`Borrador ${r.data?.version ? `v${r.data.version} ` : ''}creado`);
      navigate(`/dashboard/templates/${r.data.id}/edit`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al crear la nueva versión');
    } finally { setCreatingVersion(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-soem-primary border-t-transparent" />
    </div>
  );

  if (!template) return <div className="text-center py-12 text-slate-400">Plantilla no encontrada.</div>;

  return (
    <div className="flex flex-col h-screen bg-soem-bg" data-testid="template-builder">
      {/* Top bar */}
      <div
        className={`builder-header bg-soem-navy px-5 py-3 flex items-center justify-between
          flex-shrink-0 shadow-md z-10`}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard/templates')}
            className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-white/10" data-testid="back-btn">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-bold text-sm">{template.name}</span>
              {template.version && (
                <span className="text-xs text-slate-300 font-mono">v{template.version}</span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${templateStatusConfig(template.status).classes}`}>
                {templateStatusConfig(template.status).label}
              </span>
            </div>
            {savedAt && <p className="text-slate-400 text-xs">Guardado {savedAt.toLocaleTimeString('es-MX')}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPreview(true)}
            className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="preview-btn">
            <Eye size={14} /> Vista previa
          </button>
          {/* #RecintoSeguro — Importar criterios desde archivo JSON.
              #Fase0-B3 — solo en borrador: el import también crea secciones y
              preguntas, así que sobre una plantilla activa cambiaba en
              caliente las evaluaciones en curso. La API ahora lo rechaza
              fuera de borrador. */}
          {!readOnly && actor?.role === 'super_admin' && (
            <>
              <input
                ref={jsonFileRef}
                type="file"
                accept=".json,application/json"
                onChange={handleImportJsonFile}
                style={{ display: 'none' }}
              />
              <button onClick={handleImportJsonClick} disabled={importingJson}
                className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50"
                data-testid="import-json-btn"
                title="Importar enfoque, recinto seguro y niveles desde un archivo JSON de criterios">
                {importingJson
                  ? <span className="animate-spin rounded-full h-3 w-3 border-2 border-slate-400 border-t-transparent" />
                  : <Upload size={14} />}
                Importar JSON
              </button>
            </>
          )}
          {owned && canCreateNewVersion(template.status) && (
            <button onClick={handleNewVersion} disabled={creatingVersion}
              className="soem-btn-primary px-4 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60" data-testid="new-version-btn"
              title="Crea un borrador editable a partir de esta plantilla; al activarlo, esta versión queda retirada">
              <GitBranch size={14} /> {creatingVersion ? 'Creando...' : 'Nueva versión'}
            </button>
          )}
          {!readOnly && (
            <button onClick={() => setShowActivate(true)}
              className="soem-btn-primary px-4 py-1.5 text-xs flex items-center gap-1.5" data-testid="activate-btn">
              <CheckCircle size={14} /> Activar plantilla
            </button>
          )}
        </div>
      </div>

      {/* Read-only banner */}
      {readOnly && (
        <div className="bg-orange-50 border-b border-orange-200 px-5 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-orange-800 text-sm">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span>
              Esta plantilla está <strong>{templateStatusConfig(template.status).label.toLowerCase()}</strong> y es de solo lectura.{' '}
              {normalizeTemplateStatus(template.status) === 'retirada'
                ? 'Ya no se usa para evaluaciones nuevas; las que ya la usan siguen con ella. '
                : ''}
              {owned && canCreateNewVersion(template.status)
                ? 'Para cambiarla crea una nueva versión (o duplícala si quieres una plantilla independiente).'
                : owned ? 'Duplícala para hacer cambios.' : 'Solo puedes editar tus propias plantillas.'}
            </span>
          </div>
          {(owned || (actor?.role === 'lider_proyecto' && template.is_official)) && <button onClick={async () => {
            try {
              const r = await templatesAPI.duplicate(template.id);
              toast.success('Copia creada');
              navigate(`/dashboard/templates/${r.data.id}/edit`);
            } catch (error) {
              toast.error(errorMessage(error, 'Error al duplicar'));
            }
          }} className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5 flex-shrink-0">
            <Copy size={12} /> {template.is_official ? 'Duplicar plantilla oficial' : 'Duplicar'}
          </button>}
        </div>
      )}

      <nav className="builder-tabs" aria-label="Constructor móvil">
        {[
          ['sections', 'Secciones'], ['questions', 'Preguntas'], ['editor', 'Configurar pregunta'],
        ].map(([key, label]) => (
          <button key={key} aria-pressed={mobileTab === key} onClick={() => setMobileTab(key)}>{label}</button>
        ))}
      </nav>
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT: Sections */}
        <div className={`builder-panel ${mobileTab === 'sections' ? 'is-current' : ''}
          w-60 bg-soem-navy flex flex-col flex-shrink-0 overflow-hidden`}>
          <div className="px-3 py-3 border-b border-white/10">
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
              <Layers size={12} /> Secciones ({template.sections?.length || 0})
            </p>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
            <SortableContext items={(template.sections || []).map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
                {(template.sections || []).map(sec => (
                  <SortableSection key={sec.id} section={sec}
                    active={sec.id === activeSectionId} readOnly={readOnly || reordering || hasLocked(sec)}
                    onClick={() => {
                      setActiveSectionId(sec.id);
                      setMobileTab('questions');
                      // Auto-select first question of the clicked section
                      const firstQ = sec.questions?.[0];
                      setActiveQuestionId(firstQ?.id || null);
                    }}
                    onDelete={() => deleteSection(sec.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {!readOnly && (
            <div className="px-3 py-3 border-t border-white/10 space-y-2">
              <button onClick={addSection} disabled={addingSection}
                className="w-full flex items-center justify-center gap-2 py-2 text-xs text-slate-300 hover:text-white border border-white/20 rounded-sm hover:border-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-section-btn">
                {addingSection ? <span className="animate-spin rounded-full h-3 w-3 border-2 border-slate-300 border-t-transparent" /> : <Plus size={13} />}
                {addingSection ? 'Agregando...' : 'Agregar seccion'}
              </button>
            </div>
          )}
        </div>

        {/* CENTER: Questions */}
        <div className={`builder-panel ${mobileTab === 'questions' ? 'is-current' : ''}
          flex-1 flex flex-col overflow-hidden border-r border-slate-200`}>
          {!activeSection ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <Layers size={40} className="mx-auto mb-3 text-slate-200" />
                <p className="font-medium">Selecciona o crea una sección</p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-3 flex-shrink-0">
                {editingSection === activeSection.id ? (
                  <form onSubmit={e => { e.preventDefault(); saveSectionName(activeSection.id, editingSection.name || activeSection.name); }} className="flex gap-2 flex-1">
                    <input autoFocus className="flex-1 px-3 py-1.5 border border-soem-primary rounded-sm text-sm focus:outline-none"
                      defaultValue={activeSection.name}
                      onBlur={e => saveSectionName(activeSection.id, e.target.value)}
                      onKeyDown={e => { if (e.key === 'Escape') setEditingSection(null); }} />
                  </form>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <h2 className="font-bold text-soem-navy">{activeSection.name}</h2>
                    {!readOnly && (
                      <button onClick={() => setEditingSection(activeSection.id)} className="text-slate-400 hover:text-soem-primary"><Edit2 size={13} /></button>
                    )}
                  </div>
                )}
                <SectionWeightEditor
                  key={activeSection.id}
                  section={activeSection}
                  sections={template.sections}
                  disabled={readOnly}
                  onSave={saveSectionWeight}
                />
                <span className="text-xs text-slate-400">{activeSection.questions?.length || 0} preguntas</span>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQuestionDragEnd}>
                <SortableContext items={(activeSection.questions || []).map(q => q.id)} strategy={verticalListSortingStrategy}>
                  <div className="flex-1 overflow-y-auto">
                    {(activeSection.questions || []).length === 0 ? (
                      <div className="flex items-center justify-center h-full text-slate-400 flex-col gap-2">
                        <Star size={36} className="text-slate-200" />
                        <p className="text-sm">Sin preguntas aún</p>
                        {!readOnly && <p className="text-xs">Haz clic en "+ Agregar pregunta"</p>}
                      </div>
                    ) : readOnly ? (
                      /* In readOnly mode, render simple clickable rows (no DnD interference) */
                      (activeSection.questions || []).map(q => (
                        <QuestionRow key={q.id} question={q}
                          active={q.id === activeQuestionId} readOnly={true}
                          onClick={() => {
                            setActiveQuestionId(q.id);
                            setMobileTab('editor');
                          }}
                          onDuplicate={() => {}} onDelete={() => {}} />
                      ))
                    ) : (
                      (activeSection.questions || []).map(q => (
                        <SortableQuestion key={q.id} question={q}
                          active={q.id === activeQuestionId} readOnly={reordering || questionLocked(actor, q)}
                          onClick={() => {
                            setActiveQuestionId(q.id);
                            setMobileTab('editor');
                          }}
                          onDuplicate={() => duplicateQuestion(q)}
                          onDelete={() => deleteQuestion(q.id)} />
                      ))
                    )}
                  </div>
                </SortableContext>
              </DndContext>
              {!readOnly && (
                <div className="px-4 py-3 border-t border-slate-200 bg-white flex-shrink-0">
                  <button onClick={addQuestion} disabled={addingQuestion}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm text-soem-primary border-2 border-dashed border-blue-200 rounded-sm hover:border-soem-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="add-question-btn">
                    {addingQuestion ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-soem-primary border-t-transparent" /> : <Plus size={14} />}
                    {addingQuestion ? 'Agregando...' : 'Agregar pregunta'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT: Question Editor */}
        <div className={`builder-panel builder-editor ${mobileTab === 'editor' ? 'is-current' : ''}
          w-96 flex-shrink-0 bg-white overflow-hidden flex flex-col`}>
          <button className="builder-close" onClick={() => setMobileTab('questions')}>Volver a Preguntas</button>
          {activeQuestion ? (
            // #Fase0-B3 — la llave incluye la plantilla: una "nueva versión"
            // conserva los ids de las preguntas y, sin esto, el editor no se
            // reiniciaba al saltar del original (solo lectura) al borrador.
            <QuestionEditor key={`${id}-${activeQuestion.id}`} question={activeQuestion}
              previousQuestions={previousQuestions}
              allQuestions={allQuestions}
              tid={id} sid={activeSectionId} onSaved={handleQuestionSaved}
              readOnly={readOnly || questionLocked(actor, activeQuestion)} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 flex-col gap-2 p-6 text-center">
              <Settings size={36} className="text-slate-200" />
              <p className="text-sm">
                {readOnly ? 'Haz clic en una pregunta para ver su configuración' : 'Selecciona una pregunta para editarla'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showPreview && <FormPreview template={template} onClose={() => setShowPreview(false)} />}
      {showActivate && <ActivateModal template={template}
        allowDefault={actor?.role === 'super_admin' && !template.owner_id}
        onActivate={handleActivate} onClose={() => setShowActivate(false)} />}
    </div>
  );
}
